<script setup lang="ts">
import { useRoute, useRouter, RouterLink } from "vue-router";
import { ref, onMounted, computed } from "vue";
import { storeToRefs } from "pinia";
import { open_in_new, envelope } from "pn-design-assets/pn-assets/icons";
import RvApi, { type OrderStatus } from "@/rvapi";
import type { Order, Postcard } from "@/rvapi";
import config from "@/config";
import { utc2local } from "@/utils";
import OrderLogList from "@/components/OrderLogList.vue";
import LogWriter from "@/components/LogWriter.vue";
import { useAuthStore } from "@/stores/auth";
import ArmedButton from "@/components/ArmedButton.vue";
import ResendReceipt from "@/components/ResendReceipt.vue";
import PostcardDetails from "@/components/order/PostcardDetails.vue";

const alertOnStatuses = [
  "registered",
  "declined",
  "cancelled",
] as OrderStatus[];
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const { role } = storeToRefs(auth);
const orderId = route.params.id as string;
const order = ref({} as Order);
const postcards = ref([] as Postcard[]);
const history = ref(false);
const loaded = ref(false);
const notfound = ref(false);

const canResend = computed(() =>
  postcards.value.some((p) => p.receivers.some((r) => r.resendCheck)),
);
const resendOrderState = ref("normal");

onMounted(async () => {
  const result = await RvApi.getPaymentInfo(orderId);
  if (result.order) {
    notfound.value = false;
    order.value = result.order;
  } else {
    notfound.value = true;
  }
  if (result.postcard) {
    postcards.value = result.postcard;
  }
  history.value = result.history;
  loaded.value = true;
});

const resend = async () => {
  loaded.value = false;
  const resends = [];
  for (const p of postcards.value) {
    const ixes: number[] = [];
    for (const r in p.receivers) {
      console.log(
        `p: ${p.cardId} - ${r} r:${p.receivers[r].name} s:${p.receivers[r].resendCheck}`,
      );
      if (p.receivers[r].resendCheck) {
        ixes.push(parseInt(r));
      }
    }
    if (ixes.length > 0) {
      resends.push(RvApi.resendCards(p._id, ixes));
    }
  }
  await Promise.all(resends);
  router.push(`/order/${orderId}/status`);
};
const resendEntireOrder = async () => {
  if (resendOrderState.value === "normal") {
    resendOrderState.value = "armed";
  } else if (resendOrderState.value === "armed") {
    for (const p of postcards.value) {
      for (const r in p.receivers) {
        p.receivers[r].resendCheck = true;
      }
    }
    await resend();
  }
};
const toggledResend = (cId: string, rId: string, value: boolean) => {
  postcards.value
    .filter((p) => p._id === cId)
    .forEach((card) => {
      card.receivers
        .filter((r) => r._id === rId)
        .forEach((r) => {
          r.resendCheck = value;
        });
    });
};
</script>

<template>
  <h1>Order {{ orderId }}</h1>
  <pn-spinner size="4" v-show="!loaded" />
  <p v-show="notfound">
    <pn-toast closable="false" text="Not found"> </pn-toast>
  </p>

  <section id="order" v-show="loaded && !notfound">
    <p v-show="history">
      <pn-toast closable="false">
        This order is older than 90 days. Limited actions can be taken on this
        order. Limited information is available for this order.
      </pn-toast>
    </p>
    <dl>
      <dd>
        <pn-button
          small="true"
          :icon="open_in_new"
          appearance="light"
          variant="borderless"
          :href="`${config.portalUrl}${orderId}`"
        >
          Open in Payment Management
        </pn-button>
      </dd>
      <dd v-show="loaded && alertOnStatuses.includes(order.orderStatus)">
        <pn-toast closable="false" appearance="error">
          This order has status {{ order.orderStatus }}
        </pn-toast>
      </dd>
      <dd v-show="role !== 'security' && !history">
        <ArmedButton
          buttonText="Resend this entire order"
          :armedText="`This will resend ${order.totalReceivers} cards, continue?`"
          @click="resendEntireOrder"
        />
      </dd>

      <dd>
        <pn-button small="true" :href="`/order/${orderId}/receipt`">
          View receipt
        </pn-button>
      </dd>
      <dd v-show="role !== 'security'">
        <ResendReceipt :orderId="orderId" :email="order.customerEmail" />
      </dd>
      <dd v-show="order.deviceId && order.deviceId !== 'web'">
        <pn-button small="true" :href="`/device/${order.deviceId}`">
          View device details
        </pn-button>
      </dd>
      <dd v-show="order.userId">
        <pn-button small="true" :href="`/user/${order.userId}`">
          View customer details
        </pn-button>
      </dd>
      <dd v-show="order.deviceId">
        <pn-button small="true" :href="`/order/${orderId}/log`">
          Add message to log
        </pn-button>
      </dd>
      <dd v-show="(role === 'dev' || role === 'security') && !history">
        <pn-button small="true" :href="`/order/${orderId}/data`">
          Data export
        </pn-button>
      </dd>
    </dl>
    <section id="order-details">
      <h3>Details</h3>
      <div class="grid-two-columns">
        <dl>
          <dt>Customer email</dt>
          <dd>
            <a :href="'mailto:' + order.customerEmail">{{
              order.customerEmail
            }}</a>
          </dd>
          <dt>Order date</dt>
          <dd>{{ utc2local(order.orderDate) }}</dd>
          <dt>Order status</dt>
          <dd>{{ order.orderStatus }}</dd>
          <dt>Total amount</dt>
          <dd>{{ order.totalAmount / 100 }} {{ order.currencyCode }}</dd>
          <dt>Payment method</dt>
          <dd>{{ order.paymentMethod }}</dd>
          <dt>Customer reference</dt>
          <dd>{{ order.customerReference }}</dd>
          <dt>Platform</dt>
          <dd>{{ order.platform }}</dd>
        </dl>
        <dl>
          <dt>Order id</dt>
          <dd>{{ order.orderId }}</dd>
          <dt>User id</dt>
          <dd>
            <a v-show="order.userId" :href="'/user/' + order.userId">{{
              order.userId
            }}</a>
            <span v-show="!order.userId">-</span>
          </dd>
          <dt>Promotion code</dt>
          <dd>{{ order.promotionCode || "-" }}</dd>
          <dt>Receipt number</dt>
          <dd>{{ order.receiptNumber || "-" }}</dd>
          <dt>Cart</dt>
          <dd>
            {{ order.cartId?.substring(0, 8) + "..." }}
          </dd>
          <dt>Device</dt>
          <dd>
            <RouterLink
              v-show="order.deviceId && order.deviceId !== 'web'"
              :to="'/device/' + order.deviceId"
            >
              {{ order.deviceId?.substring(0, 8) + "..." }}
            </RouterLink>
            <span v-show="!order.deviceId || order.deviceId === 'web'">-</span>
          </dd>
          <dt>Country / Language</dt>
          <dd>{{ order.countryCode }} / {{ order.language }}</dd>
        </dl>
      </div>
    </section>
    <section id="history" v-show="!history">
      <h2>Order history</h2>
      <pn-table bordered="true" striped="true">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
              <th>Info</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="statuschange in order.history" :key="statuschange.date">
              <td>
                {{ utc2local(statuschange.date) }}
              </td>
              <td>{{ statuschange.status }}</td>
              <td>{{ statuschange.declineExplanation }}</td>
            </tr>
          </tbody>
        </table>
      </pn-table>
    </section>
    <section id="log">
      <OrderLogList :orderId="orderId" :deviceId="order.deviceId" />
      <LogWriter
        :deviceId="order.deviceId"
        :userId="order.userId"
        :orderId="orderId"
        note="This log entry will also be visible in the device log."
      />
    </section>
    <section id="postcards">
      <h2>
        Postcards <span>({{ postcards.length }})</span>
      </h2>
      <div
        class="postcard"
        v-for="postcard in postcards"
        :key="postcard.cardId"
      >
        <PostcardDetails
          @checked="toggledResend"
          :postcard="postcard"
          :history="history"
          :expired="false"
        />
      </div>
      <div v-show="role !== 'security' && !history" class="resend">
        <pn-button
          v-show="canResend"
          small="true"
          :icon="envelope"
          v-on:click="resend"
          >Resend selected cards</pn-button
        >
        <span v-show="!canResend">Select cards to resend</span>
      </div>
    </section>
  </section>
</template>

<style lang="scss" scoped>
$gray50: #f3f2f2;

.resend {
  text-align: end;
}

.postcard {
  background-color: white;
  padding: 1rem;
  border-radius: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border: 1px solid $gray50;

  img {
    border: 1px dashed gray;
  }

  .receivers {
    grid-column: 1 / span 2;
  }

  .checkbox {
    padding: 0px;

    label {
      margin: auto;
      padding: 15px;
      display: block;
    }
  }
}

div.grid-two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
}
</style>
